import React, { useEffect, useState, useMemo } from 'react';
import styles from '../styles/AccountPage.module.css';
import NavBar from '../NavBar.js';

// Firebase
import { auth, db } from '../../firebaseConfig';
import {
  onAuthStateChanged,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, orderBy, limit   } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

function AccordionSection({ title, isOpen, onToggle, children, rightEl }) {
  return (
    <section className={styles.card}>
      <button className={styles.accHeader} onClick={onToggle} aria-expanded={isOpen}>
        <span className={styles.accTitle}>{title}</span>
        <div className={styles.accRight}>
          {rightEl}
          <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} />
        </div>
      </button>
      <div className={`${styles.accContent} ${isOpen ? styles.accOpen : ''}`}>
        <div className={styles.accInner}>{children}</div>
      </div>
    </section>
  );
}

export default function AccountPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [uid, setUid] = useState(null);

  // Perfil mostrado/ editable
  const [perfil, setPerfil] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    country: '',
    createdAt: null,
  });

  const [editMode, setEditMode] = useState(false);

  // Cambio de contraseña
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  // Estados del acordeón
  const [open, setOpen] = useState({
    info: true,
    security: false,
    balance: false,
    history: false,
    active: false,
  });

  // Estado para historial de compras
  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  // Placeholder
  const balance = useMemo(() => ({ currency: 'COP', amount: 0 }), []);

  // Función para cargar historial de compras desde receipts
// Función para cargar historial de compras con diagnóstico completo
const cargarHistorialCompras = async (userUid) => {
  try {
    setLoadingPurchases(true);
    console.log('🔍 Iniciando carga de historial para UID:', userUid);
    
    // Verificar autenticación
    if (!auth.currentUser) {
      console.error('❌ No hay usuario autenticado');
      setPurchases([]);
      return;
    }
    
    if (auth.currentUser.uid !== userUid) {
      console.error('❌ UID mismatch:', auth.currentUser.uid, '!==', userUid);
      setPurchases([]);
      return;
    }
    
    console.log('✅ Usuario autenticado correctamente');
    
    // Intentar con orderBy primero
    let receiptsSnapshot;
    try {
      console.log('📡 Intentando query con orderBy...');
      const receiptsQuery = query(
        collection(db, 'receipts'),
        where('uid', '==', userUid),
        orderBy('createdAt', 'desc')
      );
      receiptsSnapshot = await getDocs(receiptsQuery);
      console.log('✅ Query con orderBy exitosa');
    } catch (indexError) {
      console.log('⚠️ Error con orderBy (índice faltante):', indexError.message);
      console.log('🔄 Intentando sin orderBy...');
      
      const receiptsQuery = query(
        collection(db, 'receipts'),
        where('uid', '==', userUid)
      );
      receiptsSnapshot = await getDocs(receiptsQuery);
      console.log('✅ Query sin orderBy exitosa');
    }
    
    console.log(`📊 Documentos encontrados: ${receiptsSnapshot.size}`);
    
    if (receiptsSnapshot.empty) {
      console.log('📭 No hay documentos para este usuario');
      
      // Diagnóstico: verificar si existen documentos en la colección
      console.log('🔍 Verificando si existe la colección receipts...');
      const testQuery = query(collection(db, 'receipts'), limit(3));
      const testSnapshot = await getDocs(testQuery);
      console.log(`📊 Total documentos en receipts: ${testSnapshot.size}`);
      
      if (testSnapshot.size > 0) {
        console.log('📄 Ejemplos de documentos encontrados:');
        testSnapshot.forEach((doc, index) => {
          const data = doc.data();
          console.log(`Documento ${index + 1}:`, {
            id: doc.id,
            uid: data.uid,
            fecha: data.fecha,
            valorCompra: data.valorCompra
          });
        });
      }
      
      setPurchases([]);
      return;
    }
    
    // Procesar documentos encontrados
    const compras = [];
    receiptsSnapshot.forEach((doc, index) => {
      const data = doc.data();
      console.log(`📄 Procesando documento ${index + 1}/${receiptsSnapshot.size}:`, doc.id);
      
      // Validar estructura del documento
      if (!data.uid) {
        console.warn(`⚠️ Documento ${doc.id} no tiene campo 'uid'`);
        return;
      }
      
      if (data.uid !== userUid) {
        console.warn(`⚠️ Documento ${doc.id} tiene UID diferente: ${data.uid} vs ${userUid}`);
        return;
      }
      
      // Procesar productos comprados
      if (data.productosComprados && Array.isArray(data.productosComprados) && data.productosComprados.length > 0) {
        console.log(`🛍️ ${data.productosComprados.length} productos en documento ${doc.id}`);
        
        data.productosComprados.forEach((producto, prodIndex) => {
          let codigoCuenta = 'N/A';
          if (data.codigosCuentasEntregadas && data.codigosCuentasEntregadas[prodIndex]) {
            codigoCuenta = data.codigosCuentasEntregadas[prodIndex];
          } else if (data.codigosCuentasEntregadas && data.codigosCuentasEntregadas.length > 0) {
            codigoCuenta = data.codigosCuentasEntregadas[0];
          }
          
          compras.push({
            id: `${doc.id}_${prodIndex}`,
            product: `${producto.plataforma || 'N/A'} - ${producto.nombre || 'Producto'}`,
            date: formatearFecha(data.createdAt),
            amount: data.valorCompra || 0,
            status: data.estado || 'Entregado',
            medioPago: data.medioPago || 'N/A',
            cantidad: producto.cantidad || 1,
            pantallas: producto.pantallas || 'N/A',
            unidad: producto.unidad || 'N/A',
            codigoCompra: data.idmensaje || doc.id,
            codigoCuenta: codigoCuenta
          });
        });
      } else {
        // Documento sin productos específicos
        console.log(`📦 Documento ${doc.id} sin productos específicos, creando entrada general`);
        
        let codigoCuenta = 'N/A';
        if (data.codigosCuentasEntregadas && data.codigosCuentasEntregadas.length > 0) {
          codigoCuenta = data.codigosCuentasEntregadas[0];
        }
        
        compras.push({
          id: doc.id,
          product: 'Compra general',
          date: formatearFecha(data.createdAt),
          amount: data.valorCompra || 0,
          status: data.estado || 'Entregado',
          medioPago: data.medioPago || 'N/A',
          codigoCompra: data.idmensaje || doc.id,
          codigoCuenta: codigoCuenta
        });
      }
    });
    
    // Ordenar por fecha si no se pudo hacer en la query
    if (compras.length > 0) {
      compras.sort((a, b) => {
        // Intentar ordenar por fecha, si no es posible, mantener orden actual
        try {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA; // Más reciente primero
        } catch {
          return 0;
        }
      });
    }
    
    console.log(`✅ ${compras.length} compras procesadas exitosamente`);
    setPurchases(compras);
    
  } catch (error) {
    console.error('❌ Error general cargando historial:', error);
    
    // Mostrar información detallada del error
    if (error.code) {
      console.error(`Error code: ${error.code}`);
      console.error(`Error message: ${error.message}`);
      
      if (error.code === 'permission-denied') {
        console.error('🚫 Error de permisos - verificar reglas de Firebase');
      } else if (error.code === 'failed-precondition') {
        console.error('📇 Error de índice - crear índice compuesto para uid + createdAt');
      }
    }
    
    setPurchases([]);
  } finally {
    setLoadingPurchases(false);
  }
};



  // Función para formatear fecha
  const formatearFecha = (timestamp) => {
    if (!timestamp) return 'Fecha no disponible';
    
    try {
      // Si es un timestamp de Firestore
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString('es-CO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      // Si es una fecha normal
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString('es-CO', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return 'Fecha no válida';
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return 'Error en fecha';
    }
  };

useEffect(() => {
  const unsub = onAuthStateChanged(auth, async (user) => {
    if (!user) {
      console.log('❌ Usuario no autenticado, redirigiendo...');
      navigate('/');
      return;
    }
    
    console.log('✅ Usuario autenticado:', user.uid);
    setUid(user.uid);
    
    const ref = doc(db, 'Usuarios', user.uid);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      setPerfil({
        name: data.name || '',
        email: data.email || user.email || '',
        phoneNumber: data.phoneNumber || '',
        country: data.country || '',
        createdAt: data.createdAt || null,
      });
    } else {
      setPerfil((p) => ({ ...p, email: user.email || '' }));
    }
    
    // Cargar historial de compras con la nueva función
    await cargarHistorialCompras(user.uid);
    
    setLoading(false);
  });
  return () => unsub();
}, [navigate]);

  const handleSaveProfile = async () => {
    if (!uid) return;
    try {
      const nameOk = (perfil.name || '').trim().length >= 2;
      if (!nameOk) {
        alert('El nombre debe tener al menos 2 caracteres.');
        return;
      }
      const ref = doc(db, 'Usuarios', uid);
      // SOLO actualiza el nombre (no país/prefijo ni teléfono)
      await updateDoc(ref, {
        name: (perfil.name || '').trim(),
      });
      setEditMode(false);
      alert('Perfil actualizado.');
    } catch (e) {
      console.error(e);
      alert('No se pudo actualizar el perfil.');
    }
  };

  // --- Cambio de contraseña ---
  const handleChangePassword = async () => {
    try {
      if (!auth.currentUser) {
        alert('No hay usuario autenticado.');
        return;
      }
      if (!perfil.email) {
        alert('Tu cuenta no tiene email asociado para cambio de contraseña.');
        return;
      }
      if (newPw.length < 6) {
        alert('La nueva contraseña debe tener al menos 6 caracteres.');
        return;
      }
      if (newPw !== confirmPw) {
        alert('La confirmación no coincide.');
        return;
      }
      setPwBusy(true);
      const cred = EmailAuthProvider.credential(perfil.email, currentPw);
      await reauthenticateWithCredential(auth.currentUser, cred);
      await updatePassword(auth.currentUser, newPw);
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      setPwOpen(false);
      alert('Contraseña actualizada correctamente.');
    } catch (e) {
      console.error(e);
      const msg =
        e.code === 'auth/wrong-password'
          ? 'La contraseña actual es incorrecta.'
          : e.code === 'auth/too-many-requests'
          ? 'Demasiados intentos. Inténtalo más tarde.'
          : 'No se pudo actualizar la contraseña.';
      alert(msg);
    } finally {
      setPwBusy(false);
    }
  };

  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <div className={styles.spinner} />
        <p>Cargando tu cuenta…</p>
      </div>
    );
  }

  // Mostrar en un único campo: +prefijo número (solo lectura)
  const phoneDisplay =
    perfil.phoneNumber
      ? `${perfil.country || ''} ${perfil.phoneNumber}`.trim()
      : 'Sin número registrado';

  return (
    <>
      <NavBar />

      <div className={styles.wrapper}>

        {/* INFORMACIÓN PERSONAL */}
        <AccordionSection
          title="Información personal"
          isOpen={open.info}
          onToggle={() => toggle('info')}
          rightEl={
            !open.info ? null : !editMode ? (
              <button className={styles.secondaryBtn} onClick={() => setEditMode(true)}>
                Editar
              </button>
            ) : (
              <div className={styles.editGroup}>
                <button className={styles.primaryBtn} onClick={handleSaveProfile}>Guardar</button>
                <button className={styles.ghostBtn} onClick={() => setEditMode(false)}>Cancelar</button>
              </div>
            )
          }
        >
          <div className={styles.formGrid}>
            <div className={styles.formItem}>
              <label>Nombre</label>
              <input
                type="text"
                value={perfil.name}
                disabled={!editMode}
                onChange={(e) => setPerfil({ ...perfil, name: e.target.value })}
              />
            </div>

            <div className={styles.formItem}>
              <label>Correo</label>
              <input type="email" value={perfil.email} disabled />
            </div>

            <div className={styles.formItem}>
              <label>Teléfono</label>
              <input type="text" value={phoneDisplay} disabled />
            </div>
          </div>
        </AccordionSection>

        {/* SEGURIDAD */}
        <AccordionSection
          title="Seguridad"
          isOpen={open.security}
          onToggle={() => toggle('security')}
        >
          {/* Solo cambio de contraseña */}
          <div className={styles.sectionHeaderLite}>
            <h3 className={styles.sectionTitleLite}>Contraseña</h3>
            <button
              className={styles.secondaryBtn}
              onClick={() => setPwOpen((v) => !v)}
            >
              {pwOpen ? 'Ocultar' : 'Cambiar contraseña'}
            </button>
          </div>

          {pwOpen && (
            <div className={styles.formGrid}>
              <div className={styles.formItem}>
                <label>Contraseña actual</label>
                <input
                  type="password"
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  placeholder="Ingresa tu contraseña actual"
                />
              </div>
              <div className={styles.formItem}>
                <label>Nueva contraseña</label>
                <input
                  type="password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div className={styles.formItem}>
                <label>Confirmar nueva contraseña</label>
                <input
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  placeholder="Repite la nueva contraseña"
                />
              </div>
              <div className={styles.formItem}>
                <button
                  className={styles.primaryBtn}
                  disabled={pwBusy}
                  onClick={handleChangePassword}
                >
                  {pwBusy ? 'Guardando…' : 'Guardar contraseña'}
                </button>
              </div>
            </div>
          )}
        </AccordionSection>

        {/* SALDO */}
        <AccordionSection
          title="Saldo"
          isOpen={open.balance}
          onToggle={() => toggle('balance')}
        >
          <div className={styles.balanceRow}>
            <div>
              <div className={styles.balanceLabel}>Saldo disponible</div>
              <div className={styles.balanceValue}>
                {balance.currency} {balance.amount.toLocaleString('es-CO')}
              </div>
            </div>
            <div className={styles.balanceActions}>
              <button className={styles.primaryBtn} onClick={() => alert('Función de recarga próximamente')}>
                Recargar
              </button>
              <button className={styles.ghostBtn} onClick={() => alert('Movimientos de saldo próximamente')}>
                Ver movimientos
              </button>
            </div>
          </div>
        </AccordionSection>

        {/* HISTORIAL DE COMPRAS */}
        <AccordionSection
          title={`Historial de compras ${purchases.length > 0 ? `(${purchases.length})` : ''}`}
          isOpen={open.history}
          onToggle={() => toggle('history')}
          rightEl={
            open.history && purchases.length > 0 ? (
              <button
                className={styles.secondaryBtn}
                onClick={() => cargarHistorialCompras(uid)}
                disabled={loadingPurchases}
              >
                {loadingPurchases ? 'Actualizando...' : 'Actualizar'}
              </button>
            ) : null
          }
        >
          {loadingPurchases ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div className={styles.spinner} style={{ margin: '0 auto 10px' }} />
              <p>Cargando historial...</p>
            </div>
          ) : purchases.length === 0 ? (
            <p className={styles.helpText}>Aún no hay compras registradas.</p>
          ) : (


          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Fecha</th>
                  <th>Medio de Pago</th>
                  <th>Monto</th>
                  <th>Estado</th>
                  <th>Código Cuenta</th> 
                  <th>Código Transacción</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div>
                        <strong>{p.product}</strong>
                        {p.cantidad && p.cantidad > 1 && (
                          <small style={{ display: 'block', color: '#666' }}>
                            Cantidad: {p.cantidad}
                          </small>
                        )}
                        {p.pantallas && p.pantallas !== 'N/A' && (
                          <small style={{ display: 'block', color: '#666' }}>
                            Pantallas: {p.pantallas} ({p.unidad})
                          </small>
                        )}
                      </div>
                    </td>
                    <td>{p.date}</td>
                    <td>
                      <span style={{ 
                        fontSize: '0.75em', 
                        background: '#0041a3ff', 
                        color: '#fff',
                        padding: '4px 8px', 
                        borderRadius: '4px',
                        fontFamily: 'monospace'
                      }}>
                        {p.medioPago}
                      </span>
                    </td>
                    <td>
                      <strong>
                        {typeof p.amount === 'number' && p.amount > 1000 ? 'COP ' : '$'}
                        {Number(p.amount || 0).toLocaleString('es-CO')}
                      </strong>
                    </td>
                    <td>
                      <span style={{
                        color: p.status === 'Entregado' ? '#4caf50' : '#ff9800',
                        fontWeight: 'bold'
                      }}>
                        {p.status}
                      </span>
                    </td>
                    {/* 🔥 NUEVA CELDA PARA CÓDIGO DE CUENTA */}
                    <td>
                      <code style={{ 
                        fontSize: '0.75em', 
                        background: '#008cffff', 
                        color: '#fff',
                        padding: '4px 8px', 
                        borderRadius: '4px',
                        fontFamily: 'monospace'
                      }}>
                        {p.codigoCuenta}
                      </code>
                    </td>
                    <td>
                      <code style={{ 
                        fontSize: '0.75em', 
                        background: '#333', 
                        color: '#fff',
                        padding: '4px 8px', 
                        borderRadius: '4px',
                        fontFamily: 'monospace'
                      }}>
                        {p.codigoCompra?.slice(0, 30) || 'N/A'}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
</div>



          )}
        </AccordionSection>

        {/* CUENTAS ACTIVAS */}
        <AccordionSection
          title="Cuentas activas"
          isOpen={open.active}
          onToggle={() => toggle('active')}
          rightEl={
            open.active ? (
              <button
                className={styles.primaryBtn}
                onClick={() => navigate('/activate-accounts')}
              >
                Abrir página
              </button>
            ) : null
          }
        >
          <p className={styles.helpText}>
            Consulta aquí las cuentas que tienes activas actualmente y gestiona sus detalles.
          </p>
          <button
            className={styles.secondaryBtn}
            onClick={() => navigate('/activate-accounts')}
          >
            Ir a Cuentas activas
          </button>
        </AccordionSection>

        <footer className={styles.footer}>
          © 2025 BearMax - Todos los derechos reservados
        </footer>
      </div>
    </>
  );
}